import { useCallback, useEffect, useState } from "react";

import { useLocation, useHistory } from "react-router-dom";

import { AccountOnHoldCheckoutMessage } from "@/components/AccountOnHold";
import { useAppState } from "@/components/AppStateProvider";
import Box from "@/components/Box";
import CircleButton from "@/components/CircleButton";
import Grid from "@/components/Grid";
import GridTestProductCard from "@/components/GridTestProductCard";
import Loading from "@/components/Loading";
import { ProductFiltersDesktop, ProductFiltersMobile } from "@/components/ProductFiltersSidebar";
import StackedTestProductCard from "@/components/StackedTestProductCard";
import { useLocalesContext } from "@/contexts/Locale";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { COMING_SOON, OUT_OF_STOCK } from "@/core/constants";
import { TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION } from "@/graphql/accounts";
import {
  PRACTITIONER_TEST_PRODUCT_CONNECTIONS_QUERY,
  PRACTITIONER_TEST_PRODUCT_DETAIL_QUERY,
  SHOP_LISTING_FILTERS_QUERY
} from "@/graphql/shop";
import { useDebounce } from "@/hooks/useDebounce";
import useListControls from "@/hooks/useListControls";
import useUserProfile from "@/hooks/useUserProfile";
import LifeStyleIcon from "@/images/LifeStyleIcon";
import { GilroyFontCurrency } from "@/tpo/Currency";
import GridIcon from "@/tpo/GridIcon";
import Group from "@/tpo/Group";
import InfiniteScroll, { InfiniteScrollContext } from "@/tpo/InfiniteScroll";
import ListIcon from "@/tpo/ListIcon";
import SortMenu from "@/tpo/SortMenu";
import Stack from "@/tpo/Stack";
import { Search } from "@/tpo/TextInput";
import ListPageTemplate from "@/tpo/shop/ListPageTemplate";
import useProductFilters from "@/tpo/shop/hooks/useProductFilters";
import useTestProducts from "@/tpo/shop/hooks/useTestProducts";
import ButtonV2, { ActionIcon } from "@/v2/Buttons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useTheme } from "styled-components";

import { CMP_PERMISSION_PURCHASE_PRODUCTS } from "../constants";
import FeaturedTestProductsCarousel from "./FeaturedTestProductsCarousel";
import LocaleSwitcher from "./ui/LocaleSwitcher";

const PRODUCT_FILTER_PARAM_KEYS = ["gender", "categories", "sampleTypes", "testingServices"];

function hasFilterParamsInSearch(search) {
  const params = new URLSearchParams(search);
  return PRODUCT_FILTER_PARAM_KEYS.some(key => params.has(key));
}

function TestProductListContent({
  sampleTypes = [],
  categories = [],
  testingServices = [],
  filtersLoaded = true
}) {
  const theme = useTheme();

  const { patientProfile, practitionerProfile } = useUserProfile();

  const showReportsTab = practitionerProfile?.showReportsTab;

  const history = useHistory();
  const location = useLocation();

  const { locale } = useLocalesContext();
  const { organisation } = useOrganisationContext();
  const searchParams = new URLSearchParams(location.search);
  const search = searchParams.get("search") || "";
  const filterByFavourites = searchParams.get("filterByFavourites") === "true";

  const debouncedSearch = useDebounce(search, 200);

  const controls = useListControls({
    sortOptions: [
      {
        label: "Recommended",
        value: "sort_order"
      },
      {
        label: "A - Z",
        value: "name"
      },
      {
        label: "Z - A",
        value: "-name"
      },
      {
        label: "Price Asc",
        value: "trade_current_price"
      },
      {
        label: "Price Desc",
        value: "-trade_current_price"
      }
    ]
  });

  const { toggleFilter, resetFilters, allFilters, validatedFilters } = useProductFilters({
    defaultFilters: {
      gender: "all",
      categories: [],
      sampleTypes: [],
      testingServices: []
    },
    allFilters: {
      gender: ["all", "female", "male"],
      categories: [...categories].sort(),
      testingServices: [...testingServices].sort(),
      sampleTypes: [...sampleTypes].sort()
    },
    filterSelected: {
      gender: ["male", "female"]
    }
  });

  const { addProductToBasket, basket, hasBasketGotProduct, removeProductFromBasket } =
    useOrganisationBasketContext();

  const setFilterByFavourites = useCallback(
    value => {
      const next = typeof value === "function" ? value(filterByFavourites) : value;
      const params = new URLSearchParams(location.search);
      if (next) {
        params.set("filterByFavourites", "true");
      } else {
        params.delete("filterByFavourites");
      }
      history.push({
        pathname: location.pathname,
        search: params.toString()
      });
    },
    [filterByFavourites, history, location.pathname, location.search]
  );

  const {
    products: { items, hasMore, endCursor },
    loading,
    fetchMore
  } = useTestProducts({
    query: PRACTITIONER_TEST_PRODUCT_CONNECTIONS_QUERY,
    queryVariables: {
      organisation: organisation?.id,
      filterByFavourites
    },
    connectionsFieldName: "practitionerTestProductConnections",
    validatedFilters,
    search: debouncedSearch,
    sort: controls.sort,
    isoCode: locale?.isoCode,
    paymentIsoCode: basket?.paymentLocale?.isoCode,
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    skip: !filtersLoaded && hasFilterParamsInSearch(location.search)
  });

  const { selectTestProductId } = useTestProductModalContext();
  const { setPractitionerBasketOpen: setBasketOpen } = useAppState();

  const handleAddToBasket = useCallback(
    testProduct => {
      if (!basket) return;
      if (
        basket.multiplePatientsEnabled &&
        testProduct.isCyrex &&
        !hasBasketGotProduct(testProduct.id)
      ) {
        return;
      }
      const allowMoreThanOne = basket?.purchaseAsStock || basket?.multiplePatientsEnabled;
      if (testProduct.options?.length || (!allowMoreThanOne && testProduct.addons?.length)) {
        selectTestProductId(testProduct.id);
        return;
      }
      addProductToBasket(testProduct.id);
      selectTestProductId(null);
      setBasketOpen(true);
    },
    [addProductToBasket, selectTestProductId, setBasketOpen, basket, hasBasketGotProduct]
  );

  const handleRemoveFromBasket = useCallback(
    testProduct => {
      const line = basket.productLineItems.find(line => line.product.id === testProduct.id);
      // clinic location should not be present within the context this function is called
      // but add anyway to be explicit
      removeProductFromBasket(line.product.id, line.productOption?.id, line.clinicLocation?.id);
    },
    [removeProductFromBasket, basket]
  );

  const [toggleFavouriteMutation] = useMutation(TOGGLE_TEST_PRODUCT_FAVOURITE_MUTATION);

  const toggleFavourite = useCallback(
    testProductId => {
      toggleFavouriteMutation({
        variables: {
          testProductId
        }
      });
    },
    [toggleFavouriteMutation]
  );

  const [view, setView] = useState("grid");
  const [card, setCard] = useState("grid");

  useEffect(() => {
    function handleMobile() {
      if (window.innerWidth <= 960 && card != "grid") {
        setCard("grid");
      } else if (window.innerWidth > 960 && view === "list" && card !== "list") {
        setCard("list");
      }
    }
    window.addEventListener("resize", handleMobile);
    return () => {
      window.removeEventListener("resize", handleMobile);
    };
  }, [card, setCard, view]);

  const permissions = organisation?.permissions || [];

  const infiniteScrollContent = (
    <InfiniteScrollContext.Consumer>
      {({ itemsList, setBottomElement }) => (
        <>
          {itemsList.map((testProduct, idx) => {
            let addText = "Add";
            const allowMoreThanOne = basket?.purchaseAsStock || basket?.multiplePatientsEnabled;

            if (testProduct.options?.length || (!allowMoreThanOne && testProduct.addons?.length)) {
              addText = "Options";
            } else if (!allowMoreThanOne && hasBasketGotProduct(testProduct.id)) {
              addText = "Added";
            }

            if (testProduct.status === OUT_OF_STOCK) {
              addText = "Out of stock";
            } else if (testProduct.status === COMING_SOON) {
              addText = "Coming soon";
            }

            if (!testProduct.isStudentAllowed) {
              addText = "Not available";
            }

            const onAdd =
              allowMoreThanOne || (!allowMoreThanOne && !hasBasketGotProduct(testProduct.id))
                ? e => {
                    e.onAdd = true;
                    if (allowMoreThanOne || !hasBasketGotProduct(testProduct.id)) {
                      handleAddToBasket(testProduct);
                    }
                  }
                : undefined;

            const cyrexBlockedForMultiPatient =
              basket?.multiplePatientsEnabled &&
              testProduct.isCyrex &&
              !hasBasketGotProduct(testProduct.id);

            const addDisabled =
              testProduct.status === OUT_OF_STOCK ||
              testProduct.status === COMING_SOON ||
              !testProduct.isStudentAllowed ||
              cyrexBlockedForMultiPatient ||
              (!allowMoreThanOne && hasBasketGotProduct(testProduct.id)) ||
              !permissions.includes(CMP_PERMISSION_PURCHASE_PRODUCTS);

            const onRemove =
              !allowMoreThanOne && hasBasketGotProduct(testProduct.id)
                ? e => {
                    e.onRemove = true;
                    handleRemoveFromBasket(testProduct);
                  }
                : undefined;

            const showTradeOnCard =
              testProduct.tradeCurrentPrice != null && Number(testProduct.tradeCurrentPrice) !== 0;

            const priceSection = (
              <Group
                flexWrap="wrap"
                justifyContent="space-between"
                alignItems="center"
                mt="auto"
                gap={28}
              >
                <Stack>
                  {showTradeOnCard ? (
                    <Group>
                      {testProduct.tradeFullPrice !== undefined &&
                        testProduct.tradeFullPrice > testProduct.tradeCurrentPrice && (
                          <GilroyFontCurrency
                            value={testProduct.tradeFullPrice}
                            symbol={basket?.currencySymbol}
                            data-testid="testProductListCard:tradeFullPrice"
                            strikethrough
                            color="dark"
                          />
                        )}
                      <GilroyFontCurrency
                        value={testProduct?.tradeCurrentPrice}
                        symbol={basket?.currencySymbol}
                        data-testid="testProductListCard:tradeCurrentPrice"
                        color={
                          testProduct.tradeFullPrice !== undefined &&
                          testProduct.tradeFullPrice > testProduct.tradeCurrentPrice
                            ? "red"
                            : "dark"
                        }
                      />
                    </Group>
                  ) : null}
                  {showTradeOnCard ? (
                    <GilroyFontCurrency
                      prefix="RRP"
                      color="midGrey"
                      fontSize={[16, 16, 18]}
                      decimalFontSize={[16, 16, 18]}
                      symbol={basket?.currencySymbol}
                      value={testProduct.exvatFullPrice}
                      data-testid="testProductListCard:retailFullPrice"
                    />
                  ) : null}
                </Stack>
                {onAdd && (
                  <ButtonV2
                    withChevron
                    onClick={onAdd}
                    disabled={addDisabled}
                    color={addDisabled ? "midGrey" : "green"}
                    size={["sm", "sm", "md"]}
                  >
                    {addText}
                  </ButtonV2>
                )}
                {onRemove && (
                  <ButtonV2 onClick={onRemove} color="red" withChevron size={["sm", "sm", "md"]}>
                    Remove
                  </ButtonV2>
                )}
              </Group>
            );

            return card === "grid" ? (
              <GridTestProductCard.Card
                onClick={e => {
                  if (e.onAdd || e.onRemove) return;
                  selectTestProductId(testProduct.id);
                }}
                ref={idx === itemsList.length - 1 ? setBottomElement : null}
                data-component-name="TestProductListCard"
                key={testProduct.id}
              >
                <GridTestProductCard.Header
                  iconSize={[40, 40, 80]}
                  iconType={testProduct.content.iconType}
                >
                  <ActionIcon
                    size={["sm", "sm", "md"]}
                    bg="haze"
                    variant="circle"
                    onClick={e => {
                      e.stopPropagation();
                      toggleFavourite(testProduct.id);
                    }}
                  >
                    <LifeStyleIcon
                      size={20}
                      outline={!patientProfile?.testProductFavouriteIds?.includes(testProduct.id)}
                      filled={patientProfile?.testProductFavouriteIds?.includes(testProduct.id)}
                      color={
                        patientProfile?.testProductFavouriteIds?.includes(testProduct.id)
                          ? theme.colors.genetics
                          : "black"
                      }
                    />
                  </ActionIcon>
                  <CircleButton />
                </GridTestProductCard.Header>
                <Stack gap={16} pb={60} mt={16}>
                  <GridTestProductCard.Content
                    id={testProduct.id}
                    name={testProduct.name}
                    addons={testProduct.addons}
                    laboratory={testProduct.laboratory}
                    shortDescription={testProduct.content.shortDescription}
                    testType={testProduct.content.testType}
                    categories={testProduct.content.categories}
                    centrifuge={testProduct.content.centrifuge}
                  />
                </Stack>

                {priceSection}
              </GridTestProductCard.Card>
            ) : (
              <StackedTestProductCard.Card
                onClick={e => {
                  if (e.onAdd || e.onRemove) return;
                  selectTestProductId(testProduct.id);
                }}
                ref={idx === itemsList.length - 1 ? setBottomElement : null}
                data-component-name="TestProductListCard"
                key={testProduct.id}
              >
                <Stack gap={16} pb={60}>
                  <StackedTestProductCard.Header
                    iconSize={[40, 40, 80]}
                    iconType={testProduct.content.iconType}
                    id={testProduct.id}
                    name={testProduct.name}
                    addons={testProduct.addons}
                    laboratory={testProduct.laboratory}
                    centrifuge={testProduct.content.centrifuge}
                  >
                    <ActionIcon
                      size={["sm", "sm", "md"]}
                      bg="haze"
                      variant="circle"
                      onClick={e => {
                        e.stopPropagation();
                        toggleFavourite(testProduct.id);
                      }}
                    >
                      <LifeStyleIcon
                        size={20}
                        outline={!patientProfile?.testProductFavouriteIds?.includes(testProduct.id)}
                        filled={patientProfile?.testProductFavouriteIds?.includes(testProduct.id)}
                        color={
                          patientProfile?.testProductFavouriteIds?.includes(testProduct.id)
                            ? theme.colors.genetics
                            : "black"
                        }
                      />
                    </ActionIcon>
                    <CircleButton />
                  </StackedTestProductCard.Header>
                  <StackedTestProductCard.Content
                    shortDescription={testProduct.content.shortDescription}
                    testType={testProduct.content.testType}
                    categories={testProduct.content.categories}
                  />
                </Stack>
                {priceSection}
              </StackedTestProductCard.Card>
            );
          })}
        </>
      )}
    </InfiniteScrollContext.Consumer>
  );

  const content =
    view === "grid" ? (
      <Grid gridTemplateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)", "repeat(3, 1fr)"]} gap={16}>
        {infiniteScrollContent}
      </Grid>
    ) : (
      <Stack gap={16}>{infiniteScrollContent}</Stack>
    );

  return (
    <ListPageTemplate
      // TODO - use the standard url approach involving core.urls
      urlMap={{
        supplements: `/cmp/organisations/${organisation.hashId}/dashboard/shop/supplements`,
        tests: `/cmp/organisations/${organisation.hashId}/dashboard/shop/tests`,
        ...(showReportsTab
          ? { reports: `/cmp/organisations/${organisation.hashId}/dashboard/shop/reports` }
          : {}),
        discounts: `/cmp/organisations/${organisation.hashId}/dashboard/shop/discounts`
      }}
      switcherOptions={[
        {
          label: "tests",
          value: "tests"
        },
        ...(showReportsTab ? [{ label: "reports", value: "reports" }] : []),
        {
          label: "supplements",
          value: "supplements"
        },
        {
          label: "discounts",
          value: "discounts"
        }
      ]}
      bg="haze"
      tab="tests"
      title="Find a matching test"
      subtitle="To ensure you see the correct tests available for your patient, please select the country where your patient is located. This will help you view and order the tests that are available and compliant with the regulations in their region."
      pb={[30, 30, 60]}
      centerProps={{
        stacked: true
      }}
      aboveSwitcher={
        organisation?.accountOnHold ? (
          <AccountOnHoldCheckoutMessage withGreyOuterBox={false} maxWidth={640} mb={60} />
        ) : null
      }
      filterSidebar={
        <ProductFiltersDesktop
          allFilters={allFilters}
          validatedFilters={validatedFilters}
          toggleFilter={toggleFilter}
          onReset={resetFilters}
          topContent={<LocaleSwitcher />}
        />
      }
    >
      <Stack gap={20} minWidth={0} width="100%">
        <Group
          gap={8}
          alignItems="center"
          justifyContent={["flex-start", "flex-end", "flex-end"]}
          flexWrap="wrap"
          width="100%"
        >
          <ProductFiltersMobile
            allFilters={allFilters}
            validatedFilters={validatedFilters}
            toggleFilter={toggleFilter}
            onReset={resetFilters}
            topContent={<LocaleSwitcher />}
          />
          <ActionIcon
            bg="dark"
            size={["sm", "sm", "md"]}
            variant="circle"
            flexShrink={0}
            onClick={() => setFilterByFavourites(!filterByFavourites)}
            data-testid="show-favourites-button"
          >
            <LifeStyleIcon
              size={20}
              outline={!filterByFavourites}
              filled={filterByFavourites}
              color={filterByFavourites ? theme.colors.genetics : "white"}
            />
          </ActionIcon>
          <Search
            value={search}
            onChange={s => {
              const searchParams = new URLSearchParams(location.search);
              searchParams.set("search", s);
              history.push({
                path: location.path,
                search: searchParams.toString()
              });
            }}
            maxWidth={[null, null, 350]}
            minWidth={302}
            flex="0 1 auto"
          />
          <SortMenu
            value={controls.sort}
            setValue={controls.setSort}
            open={controls.sortMenuOpen}
            setOpen={controls.setSortMenuOpen}
            options={controls.sortOptions}
            placeholder="Sort By"
          />
          <ActionIcon
            bg="white"
            size={["sm", "sm", "md"]}
            variant="circle"
            flexShrink={0}
            onClick={() => {
              const newView = view === "grid" ? "list" : "grid";
              setView(newView);

              if (newView === "grid") {
                setCard("grid");
              } else if (window.innerWidth <= 960) {
                setCard("grid");
              } else {
                setCard("list");
              }
            }}
          >
            {view === "grid" ? <ListIcon fill="black" /> : <GridIcon fill="black" />}
          </ActionIcon>
        </Group>
      </Stack>
      <Stack mt={[20, 20, 40]} gap={0} minWidth={0} width="100%">
        <FeaturedTestProductsCarousel />
        <InfiniteScroll
          loader={<Loading />}
          hasMore={hasMore}
          loading={loading}
          next={() => {
            if (endCursor) {
              fetchMore({
                variables: {
                  after: endCursor,
                  first: 10
                }
              });
            }
          }}
          items={items}
        >
          {content}
        </InfiniteScroll>
      </Stack>
    </ListPageTemplate>
  );
}

export default function TestProductListPage() {
  const location = useLocation();
  const slug = new URLSearchParams(location.search).get("slug") || "";

  const { selectTestProductId } = useTestProductModalContext();
  const { basket } = useOrganisationBasketContext();
  const { organisation } = useOrganisationContext();
  const { basketOpen } = useAppState();

  const [fetchProduct] = useLazyQuery(PRACTITIONER_TEST_PRODUCT_DETAIL_QUERY, {
    onCompleted: data => {
      if (data?.practitionerTestProduct?.id && !basketOpen) {
        selectTestProductId(data.practitionerTestProduct.id);
      }
    }
  });

  useEffect(() => {
    if (slug) {
      fetchProduct({
        variables: {
          slug,
          paymentIsoCode: basket?.paymentLocale?.isoCode,
          organisation: organisation?.id
        }
      });
    }
  }, [slug, fetchProduct, basket?.paymentLocale?.isoCode, organisation?.id]);

  const { data, loading: filtersLoading } = useQuery(SHOP_LISTING_FILTERS_QUERY);

  return (
    <>
      <TestProductListContent
        filtersLoaded={!filtersLoading}
        sampleTypes={data?.sampleTypes ?? []}
        categories={(data?.categories ?? []).map(category => category.name)}
        testingServices={data?.testingServices ?? []}
      />
    </>
  );
}
